import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getBillingItemsApi,
  getBillingItemApi,
  createBillingItemApi,
  updateBillingItemApi,
  deleteBillingItemApi,
  getActiveFeaturesApi,
  subscribeBillingItemApi,
  setBillingItemQuantityApi,
  cancelBillingItemApi,
  switchPackageApi,
  type SubscribeRequest,
  type SetQuantityRequest,
  type CancelBillingItemRequest,
  type SwitchPackageRequest,
} from "../api/billingItemsApi";
import type {
  RestBillingItem,
  RestCreateBillingItem,
  RestUpdateBillingItem,
} from "@KassaroAB/shared/types/billingItem";
import type { StoreId, BillingItemId } from "@KassaroAB/shared/types/ids";

export const useGetBillingItemsQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["stores", storeId, "billingItems"],
    queryFn: () => getBillingItemsApi(storeId!),
    enabled: !!storeId,
  });
};

export const useGetBillingItemQuery = (
  storeId: StoreId | null,
  billingItemId: BillingItemId | null,
) => {
  return useQuery({
    queryKey: ["stores", storeId, "billingItems", billingItemId],
    queryFn: () => getBillingItemApi(storeId!, billingItemId!),
    enabled: !!storeId && !!billingItemId,
  });
};

export const useGetActiveFeaturesQuery = (storeId: StoreId | null) => {
  return useQuery({
    queryKey: ["stores", storeId, "activeFeatures"],
    queryFn: () => getActiveFeaturesApi(storeId!),
    enabled: !!storeId,
  });
};

const useBillingMutation = <TRequest, TResult>(
  storeId: StoreId | null,
  mutationFn: (storeId: StoreId, request: TRequest) => Promise<TResult>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: TRequest) => {
      if (storeId === null) {
        return Promise.reject(new Error("storeId is required"));
      }
      return mutationFn(storeId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "billingItems"] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "activeFeatures"] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "entitlements"] });
    },
  });
};

export const useCreateBillingItemMutation = (storeId: StoreId | null) =>
  useBillingMutation<RestCreateBillingItem, RestBillingItem>(
    storeId,
    createBillingItemApi
  );

export const useUpdateBillingItemMutation = (storeId: StoreId | null) =>
  useBillingMutation<
    { billingItemId: BillingItemId; billingItem: RestUpdateBillingItem },
    RestBillingItem
  >(storeId, (resolvedStoreId, { billingItemId, billingItem }) =>
    updateBillingItemApi(resolvedStoreId, billingItemId, billingItem)
  );

export const useDeleteBillingItemMutation = (storeId: StoreId | null) =>
  useBillingMutation<BillingItemId, void>(storeId, deleteBillingItemApi);

export const useSubscribeBillingItemMutation = (storeId: StoreId | null) =>
  useBillingMutation<SubscribeRequest, RestBillingItem>(
    storeId,
    subscribeBillingItemApi
  );

export const useSetBillingItemQuantityMutation = (storeId: StoreId | null) =>
  useBillingMutation<SetQuantityRequest, RestBillingItem[]>(
    storeId,
    setBillingItemQuantityApi
  );

export const useCancelBillingItemMutation = (storeId: StoreId | null) =>
  useBillingMutation<CancelBillingItemRequest, RestBillingItem[]>(
    storeId,
    cancelBillingItemApi
  );

export const useSwitchPackageMutation = (storeId: StoreId | null) =>
  useBillingMutation<SwitchPackageRequest, RestBillingItem[]>(
    storeId,
    switchPackageApi
  );

